import React from 'react';
import {
    Box, Typography, Paper, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, alpha,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import ScaleIcon from '@mui/icons-material/Scale';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const StyledPaper = styled(Paper)(({ theme }) => ({
    borderRadius: 20,
    overflow: 'hidden',
    border: '1px solid',
    borderColor: theme.palette.divider,
    backgroundColor: '#ffffff',
    boxShadow: '0 8px 28px rgba(0,0,0,0.06)',
    '@media print': {
        boxShadow: 'none',
        border: '1px solid #ccc',
    },
}));

const HeroHeader = styled(Box)(({ theme }) => ({
    background: `linear-gradient(135deg, ${theme.palette.primary?.dark || '#1976d2'} 0%, ${theme.palette.primary?.main || '#1976d2'} 100%)`,
    color: 'white',
    padding: theme.spacing(5, 4, 4),
    textAlign: 'center',
    '@media print': {
        background: 'white !important',
        color: 'black !important',
        padding: theme.spacing(3, 4, 2),
    },
}));

const AccentChip = styled(Chip)(({ theme }) => ({
    backgroundColor: '#635bff',
    color: 'white',
    fontWeight: 600,
    '@media print': {
        backgroundColor: '#635bff',
        color: 'white',
    },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
    fontWeight: 700,
    color: theme.palette.primary?.dark || 'inherit',
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: 1,
}));

const LabelCell = styled(TableCell)(({ theme }) => ({
    fontWeight: 600,
    backgroundColor: theme.palette.grey?.[100] || '#f5f5f5',
    borderRight: `1px solid ${theme.palette.divider}`,
    width: '160px',
}));

const HeaderCell = styled(TableCell)(({ theme }) => ({
    fontWeight: 700,
    backgroundColor: alpha(theme.palette.primary?.main || '#1976d2', 0.08),
    color: theme.palette.primary?.dark || 'inherit',
}));

const BasicPrint = ({ bomData = [] }) => {
    if (!Array.isArray(bomData) || bomData.length === 0) {
        return (
            <Box sx={{ p: 6, textAlign: 'center', color: 'text.secondary' }}>
                <Typography variant="h5">No BOM data available</Typography>
            </Box>
        );
    }

    const header = bomData[0] || {};
    const foodName = header.FoodName?.trim() || 'Unknown Recipe';
    const bomId = header.FoodBomId ?? '—';
    const stdMealIncre = header.StdMealIncre ?? '—';
    const propName = header.PropName || 'Unknown Property';

    return (
        <Box
            sx={{
                maxWidth: 960,
                mx: 'auto',
                py: { xs: 3, md: 5 },
                px: { xs: 2, sm: 4, md: 6 },
                '@media print': { py: 0, px: 0, maxWidth: '100%' },
            }}
        >
            <StyledPaper>
                <HeroHeader>
                    <Typography
                        variant="h4"
                        component="h1"
                        fontWeight={800}
                        sx={{ mb: 0.5, color: '#635bff' }}
                    >
                        {propName}
                    </Typography>

                    <Typography variant="caption" sx={{ display: 'block' }}>
                        1A/4, near Ishan Ayurvedic Medical College and Research Centre, Knowledge Park I, Greater Noida, Uttar Pradesh 201310
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>
                        Email: sales@clanbridge.com | Phone: 099533 98502
                    </Typography>

                    <AccentChip
                        icon={<RestaurantIcon />}
                        label={`BOM ID: ${bomId}`}
                    />
                </HeroHeader>

                <Box sx={{ p: { xs: 3, md: 5 } }}>
                    <SectionTitle variant="h6">
                        <InfoOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
                        Recipe Summary
                    </SectionTitle>

                    <TableContainer sx={{ mb: 5, border: 1, borderColor: 'divider', borderRadius: 2, boxSizing: 'border-box' }}>
                        <Table size="small">
                            <TableBody>
                                <TableRow>
                                    <LabelCell>Food Name</LabelCell>
                                    <TableCell sx={{ padding: '6px 12px' }}>{foodName}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <LabelCell>Std Qty</LabelCell>
                                    <TableCell sx={{ padding: '6px 12px' }}>{bomData[0]?.EQNm0}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <LabelCell>Inc Qty</LabelCell>
                                    <TableCell sx={{ padding: '6px 12px' }}>{stdMealIncre}</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <SectionTitle variant="h6">
                        <ScaleIcon fontSize="small" sx={{ mr: 1 }} />
                        Ingredients
                    </SectionTitle>

                    <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 2, boxSizing: 'border-box' }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <HeaderCell sx={{ padding: '6px 12px' }}>Sr.No</HeaderCell>
                                    <HeaderCell sx={{ padding: '6px 12px' }}>Ingredient</HeaderCell>
                                    <HeaderCell sx={{ padding: '6px 12px' }}>Unit</HeaderCell>
                                    <HeaderCell sx={{ padding: '6px 12px' }}>Qty</HeaderCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {bomData.map((row, idx) => (
                                    <TableRow
                                        key={`${row.FoodBomDtlId || 'row'}-${idx}`}
                                        hover
                                        sx={{ '&:nth-of-type(odd)': { bgcolor: 'action.hover' } }}
                                    >
                                        <TableCell sx={{ padding: '6px 12px' }}>{idx + 1}</TableCell>
                                        <TableCell sx={{ padding: '6px 12px' }}>{row.ItmName || '—'}</TableCell>
                                        <TableCell sx={{ padding: '6px 12px' }}> {row.UnitName || '—'}</TableCell>
                                        <TableCell sx={{ padding: '6px 12px' }}> {row.EQ0 || '—'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Box sx={{ mt: 6, textAlign: 'center', color: 'text.secondary' }}>
                        <Typography variant="body2">
                            Generated on {new Date().toLocaleDateString()}
                        </Typography>
                    </Box>
                </Box>
            </StyledPaper>
        </Box>
    );
};

export default BasicPrint;